import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { ChevronUp, ChevronDown } from 'lucide-react';

const SortableTable = ({ columns, data, emptyMessage, containerClassName = '', className = '', initialSortKey = '', initialSortOrder = 'asc' }) => {
  const [sortKey, setSortKey] = useState(initialSortKey || columns[0]?.key || '');
  const [sortOrder, setSortOrder] = useState(initialSortOrder);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortOrder('asc');
    }
  };

  const sortedData = [...data].sort((a, b) => {
    const aValue = a[sortKey];
    const bValue = b[sortKey];
    if (aValue === undefined || bValue === undefined) return 0;
    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
    }
    return sortOrder === 'asc'
      ? String(aValue).localeCompare(String(bValue))
      : String(bValue).localeCompare(String(aValue));
  });

  return (
      <div className='border rounded-md overflowauto-referrals'>
    <div className={containerClassName}>
      <table className={`w-full divide-y divide-gray-200 ${className}`}>
        <thead className="bg-gray-50">
          <tr>
            {columns.map((col) => (
              <th
                key={col.key}
                className={`whitespace-nowrap px-6 py-4 text-left text-1x1 font-medium text-black-500  ${col.key !== 'actions' ? 'cursor-pointer hover:bg-gray-100' : ''} select-none`}
                onClick={() => col.key !== 'actions' && handleSort(col.key)}
              >
                <div className="flex items-center  gap-2">
                  {col.label}
                  {col.key !== 'actions' && (
                    <div className="flex flex-col">
                      <ChevronUp 
                        className={`w-4 h-4 ${sortKey === col.key && sortOrder === 'asc' ? 'text-blue-600' : 'text-gray-400'}`} 
                      />
                      <ChevronDown 
                        className={`w-4 h-4 -mt-1 ${sortKey === col.key && sortOrder === 'desc' ? 'text-blue-600' : 'text-gray-400'}`} 
                      />
                    </div>
                  )}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {sortedData.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className="px-6 py-4 text-center text-gray-500">
                {emptyMessage}
              </td>
            </tr>
          ) : (
            sortedData.map((row, idx) => (
              <tr
                key={row.id || idx}
                className={
                  row.completion_otp_escalated
                    ? 'bg-red-50 hover:bg-red-100'
                    : 'hover:bg-gray-50'
                }
              >
                {columns.map((col) => (
                  <td key={col.key} className="px-6 py-2 whitespace-nowrap text-1x1 text-gray-600">
                    {col.render ? col.render(row) : row[col.key]}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
      </div>
    </div>
  );
};

SortableTable.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      render: PropTypes.func,
    })
  ).isRequired,
  data: PropTypes.array.isRequired,
  emptyMessage: PropTypes.string,
  containerClassName: PropTypes.string,
  className: PropTypes.string,
  initialSortKey: PropTypes.string,
  initialSortOrder: PropTypes.oneOf(['asc', 'desc']),
};

export default SortableTable; 